// `import … from "#i18n"` in the browser and during server rendering: plain use-intl.
//
// `#i18n` is a subpath import (package.json `imports`): this module by default, ./react-server.ts
// under the `react-server` condition (the `rsc` environment). vite/messageCatalog.ts extracts the
// messages of `useExtracted()` translators imported from it.
import { use, useMemo } from "react";
import { useFormatter as useIntlFormatter } from "use-intl/react";
import { extractedTranslatorFor, translateTextWith } from "./extracted";
import { stableFormatter } from "./formatter";
import { LocaleContext } from "./LocaleContext";

export * from "use-intl/core";
export {
  IntlProvider,
  useLocale,
  useMessages,
  useNow,
  useTimeZone,
  useTranslations,
} from "use-intl/react";

/**
 * Formats the message written in the call, translated into the portal's language when it has one
 * (LocaleContext). Every call for a locale returns the same translator (./extracted.ts) rather than
 * a use-intl translator per component.
 */
export const useExtracted = (_namespace?: string) => {
  const { locale, catalog } = use(LocaleContext);
  return extractedTranslatorFor(locale, catalog);
};

export const useFormatter = () => {
  const formatter = useIntlFormatter();
  const { locale } = use(LocaleContext);
  return useMemo(() => stableFormatter(formatter, locale), [formatter, locale]);
};

/**
 * Translates text that comes from data rather than a call written here: shared and backend text
 * marked with `msg()` (shared/src/Messages.ts), such as qualifier names, permission descriptions or
 * a server error's message. Text the catalog doesn't hold, such as names people typed, is returned
 * unchanged.
 */
export const useTranslateText = () => {
  const { locale, catalog } = use(LocaleContext);
  return useMemo(() => translateTextWith(catalog), [locale, catalog]);
};
